import { bindAnalyticsInteractions, initAnalytics, trackSectorChange } from './analytics.js';
import { defaultSiteData } from './data/defaults.js';
import { renderAll } from './router.js';
import { bindSectorToggle, initSector, onSectorChange } from './sector.js';
import type { SiteData } from '../shared/types/site.js';
import { applyStaticTranslations, bindLanguageToggle, getLanguage, initLanguage, localizeSiteData, onLanguageChange } from './i18n.js';

let baseSiteData: SiteData = defaultSiteData;
let stopHeaderScrollBehavior: (() => void) | null = null;

function renderLocalized(siteData: SiteData): void {
  renderAll(localizeSiteData(siteData, getLanguage()));
  applyStaticTranslations();
}

function initHeaderScrollBehavior(): void {
  if (stopHeaderScrollBehavior) {
    stopHeaderScrollBehavior();
    stopHeaderScrollBehavior = null;
  }

  const header = document.querySelector<HTMLElement>('.header');
  if (!header) return;

  const controller = new AbortController();
  const { signal } = controller;
  let lastY = window.scrollY;
  let ticking = false;
  let hidden = false;

  const setHidden = (next: boolean) => {
    if (hidden === next) return;
    hidden = next;
    document.body.classList.toggle('header-hidden', next);
  };

  const update = () => {
    ticking = false;
    const currentY = Math.max(0, window.scrollY);
    const delta = currentY - lastY;

    if (document.body.classList.contains('nav-open') || currentY <= 24) {
      setHidden(false);
    } else if (delta > 10) {
      setHidden(true);
    } else if (delta < -8) {
      setHidden(false);
    }

    lastY = currentY;
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  };

  window.addEventListener('scroll', onScroll, { passive: true, signal });
  update();

  stopHeaderScrollBehavior = () => {
    controller.abort();
    document.body.classList.remove('header-hidden');
  };
}

initLanguage();
initSector();

function bootstrap(): void {
  initAnalytics();
  bindAnalyticsInteractions();
  initHeaderScrollBehavior();
  bindLanguageToggle();
  bindSectorToggle();
  onLanguageChange((lang) => {
    renderAll(localizeSiteData(baseSiteData, lang));
    applyStaticTranslations();
  });
  onSectorChange((sector) => {
    trackSectorChange(sector);
    renderAll(localizeSiteData(baseSiteData, getLanguage()));
    applyStaticTranslations();
  });
  renderLocalized(baseSiteData);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap);
} else {
  bootstrap();
}
